import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    CheckBox,
    DataTable,
    FormField,
    Grid,
    Select,
    Text,
    TextInput,
} from "grommet";
import { Checkmark, FormPrevious } from "grommet-icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, useScrollToTop } from "../../context";
import { ConfirmDialog } from "../../components";
import { formatCents } from "../../utils/money";
import { formatDateAu } from "../../utils/date";
import {
    deleteFunder,
    getFunder,
    getFunderAllocations,
    getFunderAudit,
    hardDeleteFunder,
    restoreFunder,
    updateFunder,
} from "../../api/funderAllocation";
import { AuditLogTable } from "./AuditLogTable";
import { DetailTabs } from "./DetailTabs";
import { Panel } from "./Panel";
import { ctaButtonProps } from "./ctaButton";
import { DetailField } from "./DetailField";

const tabularNums = { fontVariantNumeric: "tabular-nums" };
const LOAN_STATUS_OPTIONS = ["Settled", "Discharged"];
const TABS = [
    { key: "allocations", label: "Allocations" },
    { key: "audit", label: "Audit" },
];

// Grommet's default per-option rendering for a multi-select (no `children`
// given) assumes a light drop background for its selected-state styling,
// which is invisible against this page's dark theme - render it explicitly
// instead, same as the funder Select in AllocationEditor.
const renderStatusOption = (option, index, options, { active, selected }) => (
    <Box
        direction="row"
        align="center"
        justify="between"
        pad={{ vertical: "xsmall", horizontal: "small" }}
        background={selected ? "active" : active ? "surface-hover" : undefined}
    >
        <Text color="white">{option}</Text>
        {selected && <Checkmark size="small" color="white" />}
    </Box>
);

const FunderDetailSection = () => {
    const { funderId } = useParams();
    const { tokens, isInGroup } = useAuth();
    const navigate = useNavigate();
    const scrollToTop = useScrollToTop();
    const isAdmin = isInGroup("admin");

    const [activeTab, setActiveTab] = useState("allocations");

    const [funder, setFunder] = useState(null);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState({ name: "", ref: "" });
    const [saving, setSaving] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [confirmingHardDelete, setConfirmingHardDelete] = useState(false);

    const [allocations, setAllocations] = useState([]);
    const [loanStatusFilter, setLoanStatusFilter] = useState(["Settled"]);
    const [showHistorical, setShowHistorical] = useState(false);
    const [showDeletedAllocations, setShowDeletedAllocations] = useState(false);
    const [hasLoadedAllocations, setHasLoadedAllocations] = useState(false);

    const [audit, setAudit] = useState([]);
    const [auditLoading, setAuditLoading] = useState(true);

    const loadFunder = useCallback(async () => {
        if (!tokens?.idToken) return;
        try {
            const data = await getFunder(tokens.idToken, funderId);
            setFunder(data);
            setEditValue({ name: data.name, ref: data.ref || "" });
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load funder");
        }
    }, [tokens, funderId]);

    const loadAllocations = useCallback(async () => {
        if (!tokens?.idToken) return;
        try {
            const data = await getFunderAllocations(tokens.idToken, funderId, {
                loanStatus: loanStatusFilter,
                showRetired: showHistorical,
                showDeleted: showDeletedAllocations,
            });
            setAllocations(data || []);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load allocations");
        } finally {
            setHasLoadedAllocations(true);
        }
    }, [
        tokens,
        funderId,
        loanStatusFilter,
        showHistorical,
        showDeletedAllocations,
    ]);

    const loadAudit = useCallback(async () => {
        if (!tokens?.idToken) return;
        setAuditLoading(true);
        try {
            const data = await getFunderAudit(tokens.idToken, funderId);
            setAudit(data || []);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load audit trail");
        } finally {
            setAuditLoading(false);
        }
    }, [tokens, funderId]);

    useEffect(() => {
        loadFunder();
    }, [loadFunder]);
    useEffect(() => {
        loadAllocations();
    }, [loadAllocations]);
    useEffect(() => {
        if (activeTab === "audit") loadAudit();
    }, [loadAudit, activeTab]);
    useEffect(() => {
        scrollToTop();
    }, [activeTab, scrollToTop]);

    const handleSaveEdit = async () => {
        setSaving(true);
        setError(null);
        try {
            const updated = await updateFunder(tokens.idToken, funderId, {
                name: editValue.name,
                ref: editValue.ref || null,
            });
            setFunder(updated);
            setEditing(false);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to save funder");
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setEditValue({ name: funder.name, ref: funder.ref || "" });
        setEditing(false);
    };

    const handleDelete = async () => {
        setConfirmingDelete(false);
        setError(null);
        try {
            const updated = await deleteFunder(tokens.idToken, funderId);
            setFunder(updated);
            setEditing(false);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to delete funder");
        }
    };

    const handleRestore = async () => {
        setError(null);
        try {
            const updated = await restoreFunder(tokens.idToken, funderId);
            setFunder(updated);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to restore funder");
        }
    };

    const handleHardDelete = async () => {
        setConfirmingHardDelete(false);
        setError(null);
        try {
            await hardDeleteFunder(tokens.idToken, funderId);
            navigate("/funder-allocation/funders");
        } catch (e) {
            setError(
                e?.response?.data?.detail ||
                    "Failed to permanently delete funder",
            );
        }
    };

    if (!funder) {
        return <Text>{error || "Loading…"}</Text>;
    }

    return (
        <Box gap="medium">
            <Box direction="row" justify="between" align="center">
                <Button
                    icon={<FormPrevious />}
                    a11yTitle="Back to funders"
                    onClick={() => navigate("/funder-allocation/funders")}
                />
                <DetailTabs
                    tabs={TABS}
                    active={activeTab}
                    onChange={setActiveTab}
                />
            </Box>
            {error && <Text color="status-critical">{error}</Text>}

            <Panel>
                {editing ? (
                    <Box width="medium" gap="small">
                        <FormField label="Name" required>
                            <TextInput
                                value={editValue.name}
                                onChange={(e) =>
                                    setEditValue((v) => ({
                                        ...v,
                                        name: e.target.value,
                                    }))
                                }
                            />
                        </FormField>
                        <FormField label="Reference (optional)">
                            <TextInput
                                value={editValue.ref}
                                onChange={(e) =>
                                    setEditValue((v) => ({
                                        ...v,
                                        ref: e.target.value,
                                    }))
                                }
                            />
                        </FormField>
                        <Box direction="row" gap="small" wrap>
                            <Button
                                label="Save"
                                {...ctaButtonProps}
                                disabled={saving || !editValue.name}
                                onClick={handleSaveEdit}
                            />
                            <Button label="Cancel" onClick={handleCancelEdit} />
                            {funder.isActive && (
                                <Button
                                    label="Delete"
                                    color="status-critical"
                                    onClick={() => setConfirmingDelete(true)}
                                />
                            )}
                        </Box>
                    </Box>
                ) : (
                    <>
                        <Grid columns={["2fr", "1fr", "1fr"]} gap="medium">
                            <DetailField label="Name" value={funder.name} />
                            <DetailField
                                label="Reference"
                                value={funder.ref || "--"}
                            />
                            <DetailField
                                label="Status"
                                value={funder.isActive ? "Active" : "Deleted"}
                            />
                        </Grid>
                        <Box
                            direction="row"
                            gap="small"
                            margin={{ top: "small" }}
                            wrap
                        >
                            <Button
                                label="Edit"
                                onClick={() => setEditing(true)}
                            />
                            {!funder.isActive && (
                                <Button
                                    label="Restore"
                                    onClick={handleRestore}
                                />
                            )}
                            {!funder.isActive && isAdmin && (
                                <Button
                                    label="Delete permanently"
                                    color="status-critical"
                                    onClick={() =>
                                        setConfirmingHardDelete(true)
                                    }
                                />
                            )}
                        </Box>
                    </>
                )}
            </Panel>

            {activeTab === "allocations" ? (
                <Panel>
                    <Text weight="bold" size="large">
                        Allocations
                    </Text>
                    <Box direction="row" gap="medium" align="center" wrap>
                        <Box width="small">
                            <Select
                                multiple
                                closeOnChange={false}
                                placeholder="Loan status"
                                options={LOAN_STATUS_OPTIONS}
                                value={loanStatusFilter}
                                onChange={({ value }) =>
                                    setLoanStatusFilter(value)
                                }
                                messages={{ multiple: "Multiple" }}
                            >
                                {renderStatusOption}
                            </Select>
                        </Box>
                        {/* <CheckBox
                            checked={showHistorical}
                            label="Historical"
                            onChange={(e) =>
                                setShowHistorical(e.target.checked)
                            }
                        />
                        <CheckBox
                            checked={showDeletedAllocations}
                            label="Deleted"
                            onChange={(e) =>
                                setShowDeletedAllocations(e.target.checked)
                            }
                        /> */}
                    </Box>
                    {!hasLoadedAllocations ? (
                        <Text>Loading…</Text>
                    ) : allocations.length === 0 ? (
                        <Text color="dark-5">No allocations found</Text>
                    ) : (
                        <DataTable
                            columns={[
                                {
                                    property: "loanRef",
                                    header: "Reference",
                                    render: (d) => (
                                        <Text style={tabularNums}>
                                            {d.loanRef}
                                        </Text>
                                    ),
                                },
                                {
                                    property: "loanName",
                                    header: "Name",
                                    render: (d) => (
                                        <Text truncate title={d.loanName}>
                                            {d.loanName}
                                        </Text>
                                    ),
                                },
                                {
                                    property: "loanStatus",
                                    header: "Status",
                                },
                                {
                                    property: "portionCents",
                                    header: "Amount",
                                    render: (d) => (
                                        <Text style={tabularNums}>
                                            {formatCents(d.portionCents)}
                                        </Text>
                                    ),
                                },
                                {
                                    property: "effectiveDate",
                                    header: "Effective Date",
                                    render: (d) => (
                                        <Text style={tabularNums}>
                                            {formatDateAu(d.effectiveDate)}
                                        </Text>
                                    ),
                                },
                                // {
                                //     property: "retiredAt",
                                //     header: "State",
                                //     render: (d) =>
                                //         !d.isActive
                                //             ? "Deleted"
                                //             : d.retiredAt
                                //               ? "Historical"
                                //               : "Live",
                                // },
                            ]}
                            data={allocations}
                            onClickRow={({ datum }) =>
                                navigate(
                                    `/funder-allocation/loans/${datum.loanId}`,
                                )
                            }
                            sort={{ property: "loanRef", direction: "desc" }}
                            sortable
                        />
                    )}
                </Panel>
            ) : (
                <Panel>
                    <AuditLogTable loading={auditLoading} entries={audit} />
                </Panel>
            )}

            {confirmingDelete && (
                <ConfirmDialog
                    title="Delete this funder?"
                    message="It will be hidden from the default list but can be restored later."
                    onConfirm={handleDelete}
                    onCancel={() => setConfirmingDelete(false)}
                />
            )}
            {confirmingHardDelete && (
                <ConfirmDialog
                    title="Permanently delete this funder?"
                    message="This cannot be undone - the record will be removed completely, not just hidden. Blocked if it has any allocation history."
                    confirmLabel="Delete permanently"
                    onConfirm={handleHardDelete}
                    onCancel={() => setConfirmingHardDelete(false)}
                />
            )}
        </Box>
    );
};

export { FunderDetailSection };
